<template>
    <div class="auth-container">
      
      <!-- 본인인증 서비스 팝업을 호출하기 위한 폼 -->
      <form ref="formChk" method="post" action="https://nice.checkplus.co.kr/CheckPlusSafeModel/checkplus.cb" target="popupChk">
        <input type="hidden" name="m" value="service" />
        <input type="hidden" name="token_version_id" :value="tokenVersionId" />
        <input type="hidden" name="enc_data" :value="encData" />
        <input type="hidden" name="integrity_value" :value="integrity" />
        <div class="auth-button-container">
          <button type="button" class="auth-button" ref="callPop" @click="fnPopup">사용자 범용 인증서</button>
        </div>
      </form>
    </div>
</template>
  
<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, defineEmits, defineProps } from 'vue';
import loginAPI from "@/api/loginAPI";
import { useRouter } from "vue-router";
import { userStore } from "@/store/userStore";

const emit = defineEmits(['close']);
const props = defineProps<{
  businessno: string;
}>();
const store = userStore();
const router = useRouter();

const tokenVersionId = ref('');
const encData = ref('');
const integrity = ref('');
const callPop = ref<HTMLButtonElement | null>(null);
const currentOrigin = window.location.origin;
const popupWindow = ref<Window | null>(null);
const checkPopupInterval = ref<number | null>(null);
// 폼 참조
const formChk = ref<HTMLFormElement | null>(null);

// 본인인증 데이터 가져오기
const fetchAuthData = async () => {
  try {
      // API 호출 예시 (실제 구현 시 적절한 엔드포인트로 변경 필요)
      const response = await loginAPI.cryptoToken(props.businessno);
      const data = await response.data
      
      // 응답 데이터를 변수에 할당
      // reqNo = data.req_no;
      // reqDtim = data.req_dtim;
      // tokenVal = data.token_val;
      // resultVal = data.resultVal;
      // key = data.key;
      // iv = data.iv;
      // hmacKey = data.hmac_key;
      // plain = data.plain;
      tokenVersionId.value = data.token_version_id;
      encData.value = data.enc_data;
      integrity.value = data.integrity;
  } catch (error) {
      console.error('본인인증 정보 가져오기 실패:', error);
  }
};

const handleMessage = (event: MessageEvent) => {
  // event.origin과 현재 페이지 origin 출력 (디버깅용)  
  // origin 비교
  const isSameOrigin = event.origin === currentOrigin;
  // 동일 origin 또는 허용된 origin인 경우에만 메시지 처리
  if (isSameOrigin) {    
    const authResult = event.data;
    // 인증 결과에 따른 로직 처리
    if (authResult.type === 'AUTH_LOGIN_SUCCESS') {
      try {
        // JSON 문자열을 객체로 파싱 (이미 객체인 경우도 처리)
        let returnData;
        if (typeof authResult.data === 'string') {
          returnData = JSON.parse(authResult.data);
        } else {
          returnData = authResult.data;
        }
        
        emit('close',true);
        // Check if current path is login
        const currentPath = router.currentRoute.value.path;
        if (currentPath.includes('login')) {
          loginAPI
          .login(returnData)
          .then((res) => {
              store.login(res.data);
              if(res.data.가입상태 == '신청') {
                router.push({ name: "certificateApproval" });
              } else {
                router.push({ name: "home" });
              }
          })
        }
      } catch (err) {
        console.error('JSON 파싱 또는 로그인 처리 오류:', err);
      }

      // 여기서 성공시 처리 로직 수행
      // 예: 사용자 정보 저장 또는 회원가입 페이지로 이동
    } else if (authResult.type === 'AUTH_FAILURE') {
      // 인증 실패 처리
      console.log('본인인증 실패:', authResult.message || '인증에 실패했습니다.');
    }
    
    // 팝업 창 닫기
    // if (popupWindow.value && !popupWindow.value.closed) {
    //   popupWindow.value.close();
    // }
  } else {
    // 허용되지 않은 origin에서의 메시지는 무시
    console.warn('허용되지 않은 origin에서 메시지가 수신됨:', event.origin);
  }
};

// 팝업 열기 함수
const fnPopup = () => {
  window.name = "Parent_window";
  popupWindow.value = window.open('', 'popupChk', 'width=480, height=812, top=100, fullscreen=no, menubar=no, status=no, toolbar=no, titlebar=yes, location=no, scrollbar=no');

  if (formChk.value) {
      formChk.value.submit();
  }
  checkPopupInterval.value = window.setInterval(() => {
    if (popupWindow.value && popupWindow.value.closed) {
      console.log('팝업 창이 닫혔습니다.');
      
      // 팝업이 닫혔을 때 수행할 작업
      handlePopupClosed();
      
      // 인터벌 정리
      if (checkPopupInterval.value) {
        clearInterval(checkPopupInterval.value);
        checkPopupInterval.value = null;
      }
    }
  }, 500); // 0.5초마다 확인
};
const handlePopupClosed = () => {
  console.log('팝업 창 닫힘 처리 로직 실행');
  emit('close',false);
};

// 컴포넌트 마운트 시 본인인증 데이터 가져오기
onMounted(async() => {
  await fetchAuthData();
  // 메시지 이벤트 리스너 등록
  await window.addEventListener('message', handleMessage);

  await fnPopup()
});

onBeforeUnmount(() => {
  // 이벤트 리스너 제거 (메모리 누수 방지)
  window.removeEventListener('message', handleMessage);
  
  if (checkPopupInterval.value) {
    clearInterval(checkPopupInterval.value);
    checkPopupInterval.value = null;
  }
});

</script>

<style scoped>
.auth-container {
  display: none;
}

.auth-table {
width: 100%;
border-collapse: collapse;
margin-bottom: 20px;
display: none;
}

.auth-table td {
border: 1px solid #ccc;
padding: 8px 12px;
}

.label-cell {
width: 200px;
background-color: #f5f5f5;
font-weight: bold;
}

.auth-button-container {
margin-top: 20px;
text-align: center;
}

.auth-button {
  width: 341px;
  height: 60px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
  font-family: "Noto Sans CJK KR-Medium";
  background: #3382e9;
  color: white;
  border-radius: 8px;
  border: none;
}

.auth-button:hover {
background-color: #2370d4;
}
</style>